﻿import { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Button,
  MenuItem,
  Select,
  Stack,
  FormControl,
  FormControlLabel,
  FormLabel,
  TextField,
  IconButton,
  Switch,
} from "@mui/material";
import { selectWorkflow } from "@/features/homepage/workflowManagerSessionSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  selectConnectionProfile,
  selectGlobalProfile,
  updateSchedulerSettings,
} from "@/features/settings/settingsSlice";
import "@/components/ConnectionsBar/ConnectionsBar.css";
import "./WorkflowSettingsPopup.css";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { DateTimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { SettingsCard } from "./WorkflowSettingsPopup";
import { AddIcon, MinusIcon } from "@/ImageRepo";
import { setWorkflowSchedulerSettings } from "@/features/settings/settingsSlice";
import { createAlert } from "@/features/homepage/alertSliceList";
import { ShepCheckbox } from "@/components/GenericComponents/ShepComponents";

const frequencyTypes = {
  Once: 0,
  perMinute: 10,
  Hourly: 20,
  Daily: 30,
};
const RepetitionTypes = {
  perMinute: "Minutes",
  Hourly: "Hours",
  Daily: "Days",
};
const ActiveTimeRangeType = {
  Excluded: 0,
  AllDay: 10,
  Custom: 20,
};
const Weekdays = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};
function getKeyByValue(obj, value) {
  for (let key in obj) {
    if (obj[key] === value) {
      return key;
    }
  }
  return null; // Return null if value not found
}
const FormatDefaultFrequencyLabel = ({
  globalFrequencyType,
  globalFrequencyAmount,
}) => {
  const freqeuncyTypeKey = getKeyByValue(frequencyTypes, globalFrequencyType);
  const displayFrequencyType =
    globalFrequencyType === frequencyTypes.perMinute
      ? "Per Minute"
      : freqeuncyTypeKey + "";
  return (
    <>
      <Typography>Global Repeat Frequency is configured as </Typography>
      <Typography>
        <strong>
          {displayFrequencyType +
            (globalFrequencyType === frequencyTypes.Once
              ? ""
              : ", every " +
                globalFrequencyAmount +
                " " +
                RepetitionTypes[freqeuncyTypeKey])}
        </strong>
      </Typography>
    </>
  );
};
const FrequenciesSection = ({
  frequencyType,
  disabled,
  setSetting,
  frequencyAmount,
  setError,
}) => {
  return (
    <>
      <FormLabel
        className="conn-pair-options"
        style={{ marginRight: "8px", marginTop: "30px" }}
      >
        Frequency
      </FormLabel>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
        }}
      >
        <FormControlLabel
          label="Run Once"
          className="filter-checkbox-list-label"
          control={
            <ShepCheckbox
              checked={frequencyType === frequencyTypes.Once}
              disabled={disabled}
              onChange={(e) =>
                setSetting(
                  "ScheduleFrequencyType",
                  frequencyType === frequencyTypes.Once
                    ? frequencyTypes.perMinute
                    : frequencyTypes.Once,
                )
              }
            />
          }
        />
        <RepetitionSection
          disabled={disabled}
          frequencyType={frequencyType}
          repetitionAmount={frequencyAmount}
          setRepetitionAmount={(newValue) => {
            //validate the repeat every [X] section
            const regex = new RegExp("^[0-9]+$");
            if (
              frequencyType !== frequencyTypes.Once &&
              !regex.test(newValue)
            ) {
              return;
            }
            setSetting("ScheduleFrequencyAmount", newValue);
          }}
          setFrequencyType={(newValue) =>
            setSetting("ScheduleFrequencyType", newValue)
          }
        />
      </Box>
    </>
  );
};
const RepetitionSection = ({
  frequencyType,
  repetitionAmount,
  setRepetitionAmount,
  disabled,
  setFrequencyType,
}) => {
  const selectedOperationId = useSelector(selectWorkflow);
  const dispatch = useDispatch();
  return (
    <>
      <FormControlLabel
        label={
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography sx={{ paddingRight: "15px" }}>Run every </Typography>
            <TextField
              disabled={disabled || frequencyType === frequencyTypes.Once}
              style={{ width: "100px", paddingRight: "15px" }}
              value={repetitionAmount}
              onChange={(event) => {
                setRepetitionAmount(event.target.value);
              }}
              onBlur={(event) => {
                const value = Number(event.target.value);

                // for the Inventory Availability workflow
                if (
                  selectedOperationId === 102020 &&
                  value < 15 &&
                  frequencyType === 10
                ) {
                  dispatch(
                    createAlert({
                      status: "Warning",
                      message:
                        "Repeating less than every 15 minutes is not recommended.",
                    }),
                  );
                }
              }}
            />
            <Select
              disabled={disabled || frequencyType === frequencyTypes.Once}
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={frequencyType}
              label="Select Recurrence"
              onChange={(event) => {
                setFrequencyType(parseInt(event.target.value));
              }}
              MenuProps={{
                style: { zIndex: 1500 },
              }}
            >
              <MenuItem key={0} value={frequencyTypes.Once}>
                N/A
              </MenuItem>
              <MenuItem key={1} value={frequencyTypes.perMinute}>
                Minutes
              </MenuItem>
              <MenuItem key={2} value={frequencyTypes.Hourly}>
                Hours
              </MenuItem>
              <MenuItem key={3} value={frequencyTypes.Daily}>
                Days
              </MenuItem>
            </Select>
          </Box>
        }
        className="filter-checkbox-list-label"
        control={
          <ShepCheckbox
            disabled={disabled}
            checked={frequencyType !== frequencyTypes.Once}
            onChange={(e) =>
              setFrequencyType(
                frequencyType === frequencyTypes.Once
                  ? frequencyTypes.perMinute
                  : frequencyTypes.Once,
              )
            }
          />
        }
      />
    </>
  );
};

const DayActiveRangeComponent = ({
  day,
  timeRanges,
  setActiveDays,
  dayStatus,
  disabled,
}) => {
  const [dayRangeType, setDayRangeType] = useState(dayStatus);
  const [newTimeRanges, setNewTimeRanges] = useState(
    timeRanges ?? [{ StartTime: null, EndTime: null }],
  );
  const [addRangeDisabled, setAddRangeDisabled] = useState(false);
  const [removeRangeDisabled, setRemoveRangeDisabled] = useState(true);
  const dayName = getKeyByValue(Weekdays, day);
  useEffect(() => {
    if (newTimeRanges.length === 3) setAddRangeDisabled(true);
    if (newTimeRanges.length < 3) setAddRangeDisabled(false);
    if (newTimeRanges.length > 1) setRemoveRangeDisabled(false);
    if (newTimeRanges.length === 1) setRemoveRangeDisabled(true);
  }, [newTimeRanges]);

  useEffect(() => {
    setActiveDays({
      DayOfWeek: day,
      Type: dayRangeType,
      TimeRanges: newTimeRanges,
    });
  }, [newTimeRanges, dayRangeType]);

  const addNewRange = ({ startTime, endTime, key }) => {
    if (key === -1) {
      if (newTimeRanges.length < 3) {
        const ranges = newTimeRanges.concat({ StartTime: null, EndTime: null });
        setNewTimeRanges(ranges);
      }
    } else {
      //convert datetime to timespan "hh:mm:ss"
      const startTimespan =
        dayjs(startTime).get("hour") +
        ":" +
        dayjs(startTime).get("minute") +
        ":" +
        dayjs(startTime).get("second");
      const endTimespan =
        dayjs(endTime).get("hour") +
        ":" +
        dayjs(endTime).get("minute") +
        ":" +
        dayjs(endTime).get("second");
      const updatedRange = newTimeRanges.map((data, i) => {
        if (i === key) {
          return { StartTime: startTimespan, EndTime: endTimespan };
        } else return data;
      });
      setNewTimeRanges(updatedRange);
    }
  };
  const removeLastRange = () => {
    if (newTimeRanges.length > 1) {
      const updatedRange = [...newTimeRanges];
      updatedRange.pop();
      setNewTimeRanges(updatedRange);
    }
  };
  const updateActiveDayStatus = (value) => (event) => {
    if (event.target.checked) {
      setDayRangeType(value);
    } else {
      setDayRangeType(ActiveTimeRangeType.Custom);
    }
  };
  return (
    <>
      <FormLabel className="conn-pair-options" style={{ marginTop: "45px" }}>
        {dayName}
      </FormLabel>
      <FormControlLabel
        control={
          <ShepCheckbox
            disabled={disabled}
            checked={dayRangeType === ActiveTimeRangeType.AllDay}
            onChange={updateActiveDayStatus(ActiveTimeRangeType.AllDay)}
          />
        }
        label="All Day"
      />
      <FormControlLabel
        control={
          <ShepCheckbox
            disabled={disabled}
            checked={dayRangeType === ActiveTimeRangeType.Excluded}
            onChange={updateActiveDayStatus(ActiveTimeRangeType.Excluded)}
          />
        }
        label="Exclude this Day"
      />
      <Stack
        direction={"row"}
        alignItems={"center"}
        disabled={dayRangeType !== ActiveTimeRangeType.Custom}
      >
        <FormLabel>{"Run:"}</FormLabel>
        {newTimeRanges.map((range, index) => (
          <TimeRangeComponent
            timeRange={range}
            key={index}
            index={index}
            addRange={addNewRange}
            isDisabled={disabled || dayRangeType !== ActiveTimeRangeType.Custom}
          />
        ))}
        <IconButton
          className="addIconButton"
          style={{ width: "30px" }}
          onClick={() => addNewRange({ key: -1 })}
          disabled={
            disabled ||
            addRangeDisabled ||
            dayStatus !== ActiveTimeRangeType.Custom
          }
        >
          <AddIcon />
        </IconButton>
        <IconButton
          className="subtractIconButton"
          style={{ width: "30px" }}
          onClick={() => removeLastRange()}
          disabled={
            disabled ||
            removeRangeDisabled ||
            dayStatus !== ActiveTimeRangeType.Custom
          }
        >
          <MinusIcon />
        </IconButton>
      </Stack>
    </>
  );
};

const TimeRangeComponent = ({ timeRange, index, addRange, isDisabled }) => {
  dayjs.extend(utc);
  //parse timespan to display as date for timepicker component
  const startTimeComponents = timeRange.StartTime?.split(":");
  const endTimeComponents = timeRange.EndTime?.split(":");
  const startTime =
    timeRange.StartTime === null
      ? null
      : dayjs()
          .hour(startTimeComponents[0])
          .minute(startTimeComponents[1])
          .second(startTimeComponents[2]);
  const endTime =
    timeRange.EndTime === null
      ? null
      : dayjs()
          .hour(endTimeComponents[0])
          .minute(endTimeComponents[1])
          .second(endTimeComponents[2]);
  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale="en"
      localeText={{
        cancelButtonLabel: "Cancel",
        okButtonLabel: "Apply",
      }}
    >
      <Stack direction={"row"} alignItems={"center"} justifyContent={"center"}>
        <TimePicker
          disabled={isDisabled}
          label="Start Time"
          value={startTime}
          onChange={(newValue) => {
            addRange({
              startTime: newValue,
              endTime: endTime,
              key: index,
            });
          }}
          slotProps={{
            popper: { sx: { zIndex: "1500" } },
          }}
        />
        <FormLabel>To</FormLabel>
        <TimePicker
          disabled={isDisabled}
          label="End Time"
          value={endTime}
          onChange={(newValue) =>
            addRange({
              startTime: startTime,
              endTime: newValue,
              key: index,
            })
          }
          slotProps={{
            popper: { sx: { zIndex: "1500" } },
          }}
        />
      </Stack>
    </LocalizationProvider>
  );
};

const WorkflowSchedulerSettings = ({ isGlobal, onClose, workflowName }) => {
  dayjs.extend(utc);
  dayjs.extend(timezone);
  const dispatch = useDispatch();
  const selectedOperationId = useSelector(selectWorkflow);
  let connectionProfile = useSelector(selectConnectionProfile);
  let globalProfile = useSelector(selectGlobalProfile);
  let errorMessages = [];
  let list = [0];
  let workflowSchedule = isGlobal
    ? globalProfile?.WorkflowSchedule
    : connectionProfile?.WorkflowSchedule;

  const [errorText, setErrorText] = useState([]);

  const setSetting = ({ section, newValue }) => {
    dispatch(updateSchedulerSettings({ section, newValue, isGlobal }));
  };

  const AddToActiveDays = (newDay) => {
    let activeDays = isGlobal
      ? globalProfile.WorkflowSchedule.ActiveTimeRanges
      : connectionProfile.WorkflowSchedule.ActiveTimeRanges;
    const listOfDaysSettings = activeDays.map((data) => {
      if (data.DayOfWeek === newDay.DayOfWeek) return newDay;
      else return data;
    });
    setSetting({
      section: "ActiveTimeRanges",
      newValue: listOfDaysSettings,
    });
  };

  const validateData = ({ newWorkflowSchedule }) => {
    errorMessages = [];

    //validate the repeat every [X] section
    const regex = new RegExp("^[0-9]+$");
    if (
      newWorkflowSchedule.ScheduleFrequencyType !== frequencyTypes.Once &&
      !regex.test(newWorkflowSchedule.ScheduleFrequencyAmount)
    ) {
      errorMessages.push(
        'Please enter a valid number for the "Repeat every" Section',
      );
    }

    //validate time ranges
    newWorkflowSchedule.ActiveTimeRanges.forEach((day) => {
      if (day.Type === ActiveTimeRangeType.Custom) {
        const error = day.TimeRanges.some((x) => {
          const firstPass =
            x.StartTime === null ||
            x.EndTime === null ||
            (x.StartTime === "00:00:00" && x.EndTime === "00:00:00");
          if (firstPass) return firstPass;
          const startTimeComponents = x.StartTime?.split(":");
          const endTimeComponents = x.EndTime?.split(":");
          for (let i = 0; i < 3; i++) {
            if (
              parseInt(startTimeComponents[i]) == parseInt(endTimeComponents[i])
            )
              continue;
            else if (
              parseInt(startTimeComponents[i]) > parseInt(endTimeComponents[i])
            )
              return true;
            else return false;
          }
          return false; //exact same, not necessarily wrong
        });
        if (error) {
          errorMessages.push(
            "Please enter valid times for " +
              getKeyByValue(Weekdays, day.DayOfWeek),
          );
        }
      }
    });
    if (errorMessages.length > 0) {
      setErrorText(errorMessages);
      return false;
    }
    return true;
  };

  const saveConfig = () => {
    let profile = isGlobal ? globalProfile : connectionProfile;
    let newWorkflowSchedule = { ...profile.WorkflowSchedule }; //copy

    const valid = validateData({ newWorkflowSchedule });
    if (!valid) {
      return;
    }

    newWorkflowSchedule.StartTime = dayjs(newWorkflowSchedule.StartTime)
      .utc()
      .toString();

    dispatch(
      setWorkflowSchedulerSettings({
        operationId: selectedOperationId,
        connectionPairId: connectionProfile?.ConnectionPairId ?? -1,
        isGlobal,
        newWorkflowSchedule,
        timeZone: dayjs.tz.guess(),
      }),
    )
      .then((response) => {
        if (response.error !== undefined && response.error !== null) {
          console.error("Error saving option:", response.error.message);
          dispatch(
            createAlert({
              status: "Error",
              alertType: "single",
              message:
                "Unable to save settings. Error: " + response.error.message,
            }),
          );
        } else {
          dispatch(
            createAlert({
              status: "Success",
              alertType: "single",
              message: "Settings saved successfully",
            }),
          );
        }
      })
      .catch((error) => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: "Unable to save settings. Error: " + error.message,
          }),
        );
        console.error("Error saving option:", error);
      });

    // heap.io

    const eventProps = {};

    eventProps["Settings - Scope"] = isGlobal
      ? "Default"
      : "Connection-specific";
    eventProps["Settings - Workflow"] = workflowName;
    eventProps["Settings - Subgroup"] = "Workflow Scheduler";

    window.heap.track("Workflow Settings - Save Settings", eventProps);
  };

  return (
    <>
      {workflowSchedule && (
        <Stack direction={"column"}>
          <>
            <SettingsCard
              title={"Workflow Scheduler"}
              description={"Set schedules for the Workflows to run."}
              className="config-options-card scheduler-settings-card"
            >
              <>
                {
                  <FormControl style={{ width: "100%" }}>
                    <Stack
                      direction={"column"}
                      alignItems={"baseline"}
                      justifyContent={"space-between"}
                      style={{ marginTop: "30px", width: "100%" }}
                    >
                      <FormLabel
                        className="conn-pair-options"
                        style={{ marginRight: "8px" }}
                      >
                        {"Scheduler"}
                      </FormLabel>
                      <FormControlLabel
                        control={
                          <Switch
                            disabled={
                              !isGlobal & workflowSchedule.UseGlobalDisabled
                            }
                            checked={!workflowSchedule.WorkflowDisabled}
                            onChange={(event) => {
                              setSetting({
                                section: "WorkflowDisabled",
                                newValue: !event.target.checked,
                              });
                            }}
                          />
                        }
                        label="Workflow Enabled"
                      />
                      {!isGlobal ? (
                        <Stack
                          direction={"row"}
                          className="global-option-selection"
                        >
                          <FormLabel className="global-option">
                            <Typography>
                              Global Enabled Scheduler is configured as{" "}
                            </Typography>
                            <Typography>
                              <strong>
                                {!globalProfile.WorkflowSchedule
                                  .WorkflowDisabled
                                  ? "Enabled"
                                  : "Disabled"}
                              </strong>
                            </Typography>
                          </FormLabel>
                          <FormControlLabel
                            control={
                              <ShepCheckbox
                                checked={workflowSchedule.UseGlobalDisabled}
                                onChange={(event) => {
                                  setSetting({
                                    section: "UseGlobalDisabled",
                                    newValue: event.target.checked,
                                  });
                                }}
                              />
                            }
                            label="Use Global Setting"
                            style={{ marginLeft: 5, minWidth: 180 }}
                          />
                        </Stack>
                      ) : (
                        ""
                      )}

                      <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        adapterLocale="en"
                        localeText={{
                          cancelButtonLabel: "Cancel",
                          okButtonLabel: "Apply",
                        }}
                      >
                        <FormLabel
                          className="conn-pair-options"
                          style={{ marginRight: "8px", marginTop: "30px" }}
                        >
                          {"Start Date"}
                        </FormLabel>
                        <DateTimePicker
                          disabled={
                            !isGlobal && workflowSchedule.UseGlobalStartTime
                          }
                          slotProps={{
                            popper: { sx: { zIndex: "1500" } },
                          }}
                          label="Start Date"
                          value={dayjs(workflowSchedule.StartTime).local()}
                          onChange={(newValue) =>
                            setSetting({
                              section: "StartTime",
                              newValue,
                            })
                          }
                        />
                        {!isGlobal ? (
                          <Stack
                            direction={"row"}
                            className="global-option-selection"
                          >
                            <FormLabel className="global-option">
                              <Typography>
                                Global Start Date is configured as{" "}
                              </Typography>
                              <Typography>
                                <strong>
                                  {dayjs
                                    .utc(
                                      globalProfile.WorkflowSchedule.StartTime,
                                    )
                                    .local()
                                    .format("YYYY.MM.DD HH:mm:ss")}
                                </strong>
                              </Typography>
                            </FormLabel>
                            <FormControlLabel
                              control={
                                <ShepCheckbox
                                  checked={workflowSchedule.UseGlobalStartTime}
                                  onChange={(event) => {
                                    setSetting({
                                      section: "UseGlobalStartTime",
                                      newValue: event.target.checked,
                                    });
                                  }}
                                />
                              }
                              label="Use Global Setting"
                              style={{ marginLeft: 5, minWidth: 180 }}
                            />
                          </Stack>
                        ) : (
                          ""
                        )}
                      </LocalizationProvider>

                      <FrequenciesSection
                        disabled={
                          !isGlobal && workflowSchedule.UseGlobalFrequency
                        }
                        frequencyType={workflowSchedule.ScheduleFrequencyType}
                        setSetting={(prop, newValue) =>
                          setSetting({ section: prop, newValue })
                        }
                        setFrequencyType={(newValue) =>
                          setSetting({
                            section: "ScheduleFrequencyType",
                            newValue,
                          })
                        }
                        frequencyAmount={
                          workflowSchedule.ScheduleFrequencyAmount
                        }
                        setError={setErrorText}
                      />

                      {!isGlobal ? (
                        <Stack
                          direction={"row"}
                          className="global-option-selection"
                        >
                          <FormLabel className="global-option">
                            <FormatDefaultFrequencyLabel
                              globalFrequencyType={
                                globalProfile.WorkflowSchedule
                                  .ScheduleFrequencyType
                              }
                              globalFrequencyAmount={
                                globalProfile.WorkflowSchedule
                                  .ScheduleFrequencyAmount
                              }
                            />
                          </FormLabel>
                          <FormControlLabel
                            control={
                              <ShepCheckbox
                                checked={workflowSchedule.UseGlobalFrequency}
                                onChange={(event) => {
                                  setSetting({
                                    section: "UseGlobalFrequency",
                                    newValue: event.target.checked,
                                  });
                                }}
                              />
                            }
                            label="Use Global Setting"
                            style={{ marginLeft: 5, minWidth: 180 }}
                          />
                        </Stack>
                      ) : (
                        ""
                      )}

                      {workflowSchedule.ActiveTimeRanges.map((x) => {
                        return (
                          <DayActiveRangeComponent
                            disabled={
                              !isGlobal && workflowSchedule.UseGlobalTimeRange
                            }
                            key={x.DayOfWeek}
                            day={x.DayOfWeek}
                            dayStatus={x.Type}
                            timeRanges={x.TimeRanges}
                            setActiveDays={AddToActiveDays}
                          />
                        );
                      })}
                      {!isGlobal ? (
                        <Stack
                          direction={"row"}
                          className="global-option-selection"
                        >
                          <FormLabel className="global-option">
                            {
                              "Please see configuration in the Default Settings Tab"
                            }
                          </FormLabel>
                          <FormControlLabel
                            control={
                              <ShepCheckbox
                                checked={workflowSchedule.UseGlobalTimeRange}
                                onChange={(event) => {
                                  setSetting({
                                    section: "UseGlobalTimeRange",
                                    newValue: event.target.checked,
                                  });
                                }}
                              />
                            }
                            label="Use Global Setting"
                            style={{ marginLeft: 5, minWidth: 180 }}
                          />
                        </Stack>
                      ) : (
                        ""
                      )}

                      {errorText.length === 0 ? (
                        <></>
                      ) : (
                        <Typography color="red" variant="subtitle2">
                          {errorText.join("\n\r")}
                        </Typography>
                      )}
                    </Stack>
                  </FormControl>
                }
              </>
            </SettingsCard>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row-reverse",
                marginTop: "20px",
              }}
            >
              <Button
                className="btn-primary"
                onClick={() => {
                  saveConfig();
                }}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                size="large"
                sx={{
                  borderColor: "var(--primary-main)",
                  color: "var(--primary-main)",
                  p: "20px",
                  marginRight: "10px",
                  borderRadius: "10px",
                }}
                onClick={onClose}
              >
                Close
              </Button>
            </Box>
          </>
        </Stack>
      )}
    </>
  );
};

export default WorkflowSchedulerSettings;
